import * as React from 'react';
import { useTenant} from '@tyndo/hooks';
import { Button} from '@tyndo/ui-kit';
import { Building2, Check, ChevronDown} from 'lucide-react';

/**
 * Tyndo Workspace Switcher — replaces the TopBar tenant block.
 * Multisite-aware (site/plant scope) — for now, surfaces tenants and a
 *"All sites" pseudo-scope. The Multisite module can extend this later.
 */
export default function WorkspaceSwitcher() {
 const { tenant, setTenant, tenants} = useTenant();
 const [open, setOpen] = React.useState(false);
 const ref = React.useRef<HTMLDivElement>(null);

 React.useEffect(() => {
 const onClickOutside = (e: MouseEvent) => {
 if (ref.current && !ref.current.contains(e.target as Node)) {
 setOpen(false);
}
};
 document.addEventListener('mousedown', onClickOutside);
 return () => document.removeEventListener('mousedown', onClickOutside);
}, []);

 const tenantInitial = tenant?.name?.charAt(0).toUpperCase() ?? 'T';

 return (
 <div className="relative" ref={ref}>
 <Button
 type="button"
 variant="ghost"
 onClick={() => setOpen(!open)}
 className="h-auto whitespace-normal justify-start font-normal [&_svg]:size-3.5 group flex items-center gap-2 rounded-md px-2 py-1 text-sm text-foreground hover:bg-accent transition-colors"
 aria-haspopup="listbox"
 aria-expanded={open}
 >
 <span className="flex h-6 w-6 items-center justify-center rounded bg-primary/10 text-[11px] font-semibold text-primary">
 {tenantInitial}
 </span>
 <span className="flex flex-col items-start leading-none">
 <span className="text-xs text-muted-foreground t-mono uppercase tracking-wider">
 Workspace
 </span>
 <span className="text-[13px] font-medium max-w-[140px] truncate">
 {tenant?.name ?? 'Tyndo'}
 </span>
 </span>
 <ChevronDown
 className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${
 open ? 'rotate-180' : ''
}`}
 />
 </Button>

 {open && (
 <div className="absolute left-0 mt-1 w-64 rounded-lg border border-border bg-surface-overlay shadow-[var(--shadow-md)] z-50 p-1">
 <div className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
 Tenants
 </div>
 {(tenants ?? []).map((t) => {
 const active = t.tenantId === tenant?.tenantId;
 const tName = t.name ?? t.tenantId;
 return (
 <Button
 key={t.tenantId}
 type="button"
 variant="ghost"
 onClick={() => {
 setTenant(t.tenantId);
 setOpen(false);
}}
 className={`h-auto whitespace-normal justify-start text-left font-normal [&_svg]:size-4 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
 active ? 'bg-primary/10 hover:bg-primary/10 text-primary hover:text-primary' : 'text-foreground hover:bg-accent'
}`}
 >
 <span className="flex h-6 w-6 items-center justify-center rounded bg-muted text-[11px] font-semibold">
 {tName.charAt(0).toUpperCase()}
 </span>
 <span className="flex-1 text-left">
 <div className="font-medium">{tName}</div>
 <div className="text-[11px] text-muted-foreground t-mono">
 {t.tenantId}
 </div>
 </span>
 {active && <Check className="h-4 w-4 text-primary" />}
 </Button>
 );
})}
 {(!tenants || tenants.length === 0) && (
 <div className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground">
 <Building2 className="h-4 w-4" />
 No tenants available
 </div>
 )}
 </div>
 )}
 </div>
 );
}
